import { Box } from '@chakra-ui/react'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Menu, SubMenu, MenuItem, MenuDivider } from '@szhsin/react-menu'
import { colors } from '../../../ui-kit'

import { getFieldsMapping, staticFieldNames } from '../../../utils/mediaLibrary'
import type {
  CustomMetadataResponse,
  FieldIdentifier,
  SearchQueryJson,
} from '../../interfaces'
import VirtualizedCustomMetadataList from './AddFilterMenu/VirtualizedCustomMetadataList'

export interface AddFilterMenuProps {
  searchQueryJson: SearchQueryJson
  customMetadataFields: CustomMetadataResponse[]
  page: 'media-library' | 'media-collection'
  onAddFilter(fieldIdentifier: FieldIdentifier): void
}

const AddFilterMenu = (props: AddFilterMenuProps): React.ReactElement => {
  const { searchQueryJson, onAddFilter, customMetadataFields } = props
  const { t } = useTranslation()

  const existingFieldNames = useMemo(() => {
    const fieldNames: string[] = []
    if (searchQueryJson.filterGroups.length > 0) {
      searchQueryJson.filterGroups[0].expressions.forEach(expression => {
        fieldNames.push(expression.fieldName)
      })
    }
    return fieldNames
  }, [searchQueryJson])

  const filteredOtherFields = useMemo(
    () =>
      staticFieldNames.otherFields.filter(
        fieldName => !existingFieldNames.includes(fieldName),
      ),
    [existingFieldNames],
  )

  const filteredEmbeddedMetadataFields = useMemo(
    () =>
      staticFieldNames.embeddedMetadataFields.filter(
        fieldName => !existingFieldNames.includes(fieldName),
      ),
    [existingFieldNames],
  )

  const filteredCustomMetadataFields = useMemo(
    () =>
      customMetadataFields.filter(
        field => !existingFieldNames.includes(field.name),
      ),
    [customMetadataFields, existingFieldNames],
  )

  const hasEmbeddedMetadata = filteredEmbeddedMetadataFields.length > 0
  const hasCustomMetadata = filteredCustomMetadataFields.length > 0

  const handleOtherFieldClick = (fieldName: string): void => {
    onAddFilter({ fieldName })
  }

  const handleEmbeddedFieldClick = (fieldName: string): void => {
    onAddFilter({ fieldName })
  }

  const handleCustomMetadataClick = (field: CustomMetadataResponse): void => {
    onAddFilter({ customMetadataField: field })
  }

  return (
    <Menu
      portal
      menuButton={
        <Box
          as="button"
          type="button"
          paddingLeft="0.25em"
          textAlign="start"
          color="#0450d5"
          fontWeight="normal"
          cursor="pointer"
          border="solid 1px #cddcf7"
          p="6px 8px"
          borderRadius="4px"
          h="33px"
          background="#f3f8ff"
          fontFamily="inherit"
          fontSize="inherit"
          title="More filters"
        >
          {t('plus_more_filters', '+ More Filters')}
        </Box>
      }
      menuStyle={{
        padding: '0.5rem 0',
        borderRadius: '0.375rem',
        boxShadow: 'none',
        border: `1px solid ${colors.appLightGreyTwo}`,
        minWidth: 200,
        maxWidth: 400,
        zIndex: 2101, // above modal
        maxHeight: '21.5rem',
        minHeight: '21.5rem',
        overflowY: 'auto',
      }}
      menuClassName="add-filter-menu-main"
      gap={4}
      submenuCloseDelay={0}
      submenuOpenDelay={0}
    >
      {filteredOtherFields.map(fieldName => (
        <MenuItem
          key={fieldName}
          onClick={() => void handleOtherFieldClick(fieldName)}
          style={{ padding: '0.4rem 0.8rem' }}
        >
          {getFieldsMapping({ fieldName }).displayText}
        </MenuItem>
      ))}

      {hasEmbeddedMetadata && filteredOtherFields.length > 0 && <MenuDivider />}

      {hasEmbeddedMetadata && (
        <SubMenu
          portal
          position="auto"
          direction="right"
          align="start"
          gap={0}
          shift={-5}
          menuStyle={{
            padding: '0.5rem 0',
            borderRadius: 8,
            boxShadow: 'none',
            border: `1px solid ${colors.appLightGreyTwo}`,
            zIndex: 2101,
          }}
          menuClassName="add-filter-menu-embedded"
          label={
            <Box style={{ padding: '0.4rem 0.8rem', display: 'block' }}>
              {t('embedded_metadata', 'Embedded Metadata')}
            </Box>
          }
        >
          {filteredEmbeddedMetadataFields.map(fieldName => (
            <MenuItem
              key={fieldName}
              onClick={() => void handleEmbeddedFieldClick(fieldName)}
              style={{ padding: '0.4rem 0.8rem' }}
            >
              {getFieldsMapping({ fieldName }).displayText}
            </MenuItem>
          ))}
        </SubMenu>
      )}

      {hasCustomMetadata &&
        (hasEmbeddedMetadata || filteredOtherFields.length > 0) && (
          <MenuDivider />
        )}

      {hasCustomMetadata && (
        <SubMenu
          portal
          position="auto"
          direction="right"
          align="start"
          gap={0}
          shift={-5}
          menuStyle={{
            padding: '0.5rem 0',
            borderRadius: 8,
            boxShadow: 'none',
            border: `1px solid ${colors.appLightGreyTwo}`,
            width: "15rem",
            height: `${Math.min(
              filteredCustomMetadataFields.length * 32 + 16,
              340,
            )}px`,
            overflow: 'hidden',
            zIndex: 2101,
          }}
          menuClassName="add-filter-menu-custom"
          label={
            <Box style={{ padding: '0.4rem 0.8rem', display: 'block' }}>
              {t('custom_metadata', 'Custom Metadata')}
            </Box>
          }
        >
          <VirtualizedCustomMetadataList
            items={filteredCustomMetadataFields}
            onItemClick={handleCustomMetadataClick}
          />
        </SubMenu>
      )}
    </Menu>
  )
}

export default AddFilterMenu
